import styled, { keyframes } from 'styled-components';
import type { ThreadToneProfileResponse } from '../../api/types';

export function ToneProfileView({
  tone,
}: {
  tone: ThreadToneProfileResponse;
}) {
  const traits: Array<{ label: string; value?: string }> = [
    { label: '격식', value: tone.formality?.level },
    { label: '문장 길이', value: tone.sentenceStyle?.length },
    { label: '어휘', value: tone.vocabulary?.wordChoice },
    { label: '감정 강도', value: tone.tone?.emotionalIntensity },
    { label: '유머', value: tone.tone?.humor },
    { label: '태도', value: tone.tone?.attitude },
    { label: '이모지 스타일', value: tone.formatting?.emojiUsage },
    { label: '독자 호칭', value: tone.engagementStyle?.readerAddress },
  ].filter((t) => t.value);

  const confidence = tone.confidence?.score;

  return (
    <Wrap>
      {tone.summary && (
        <SummaryBlock>
          <SummaryMark aria-hidden>“</SummaryMark>
          <SummaryText>{tone.summary}</SummaryText>
        </SummaryBlock>
      )}

      {confidence != null && (
        <ConfidenceRow>
          <Ring $pct={confidence} role="meter" aria-label="말투 분석 신뢰도" aria-valuenow={Math.min(100, Math.max(0, confidence))} aria-valuemin={0} aria-valuemax={100} aria-valuetext={`${confidence}점 / 100점`}>
            <RingHole>
              <RingScore>{confidence}</RingScore>
            </RingHole>
          </Ring>
          <ConfidenceCopy>
            <ConfidenceLabel>분석 신뢰도 <ConfidenceScale>100점 기준</ConfidenceScale></ConfidenceLabel>
            <ConfidenceHint>
              {tone.confidence?.reason?.trim() ||
                '참고 글 분량과 일관성에 따라 달라집니다.'}
            </ConfidenceHint>
          </ConfidenceCopy>
        </ConfidenceRow>
      )}

      {traits.length > 0 && (
        <Section>
          <SectionTitle>말투 특징</SectionTitle>
          <TraitList>
            {traits.map((t) => (
              <TraitRow key={t.label}>
                <TraitLabel>{t.label}</TraitLabel>
                <TraitValue>{t.value}</TraitValue>
              </TraitRow>
            ))}
          </TraitList>
        </Section>
      )}

      <ChipSection title="자주 쓰는 표현" items={tone.vocabulary?.frequentExpressions} />
      <ChipSection title="시작 패턴" items={tone.openingPatterns} />
      <ChipSection title="마무리 패턴" items={tone.closingPatterns} />

      <SplitLists>
        <ListSection title="스타일 규칙" items={tone.styleRules} variant="rule" />
        <ListSection title="피해야 할 것" items={tone.avoid} variant="avoid" />
      </SplitLists>
    </Wrap>
  );
}

function ChipSection({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <ChipRow>
        {items.map((item, i) => (
          <Chip key={`${title}-${i}`}>{item}</Chip>
        ))}
      </ChipRow>
    </Section>
  );
}

function ListSection({
  title,
  items,
  variant = 'rule',
}: {
  title: string;
  items?: string[];
  variant?: 'rule' | 'avoid';
}) {
  if (!items || items.length === 0) return null;
  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <RuleList>
        {items.map((item, i) => (
          <RuleItem key={`${title}-${i}`} $variant={variant}>
            <RuleMark $variant={variant} aria-hidden>
              {variant === 'avoid' ? '×' : '✓'}
            </RuleMark>
            <span>{item}</span>
          </RuleItem>
        ))}
      </RuleList>
    </Section>
  );
}

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
  min-width: 0;
  animation: ${rise} 0.45s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SummaryBlock = styled.blockquote`
  position: relative;
  margin: 0;
  padding: 20px 22px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const SummaryMark = styled.span`
  position: absolute;
  top: 0;
  left: 10px;
  font-size: 48px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.18;
  pointer-events: none;
  user-select: none;
`;

const SummaryText = styled.p`
  position: relative;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1.7;
  overflow-wrap: anywhere;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ConfidenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Ring = styled.div<{ $pct: number }>`
  --pct: ${({ $pct }) => Math.min(100, Math.max(0, $pct))};
  width: 68px;
  height: 68px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: conic-gradient(
    ${({ theme }) => theme.colors.primary} calc(var(--pct) * 1%),
    ${({ theme }) => theme.colors.border} 0
  );
`;

const RingHole = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surface};
`;

const RingScore = styled.span`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ConfidenceCopy = styled.div`
  min-width: 0;
`;

const ConfidenceLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ConfidenceHint = styled.p`
  font-size: 13px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ConfidenceScale = styled.span`
  display: inline-block;
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TraitList = styled.dl`
  display: flex;
  flex-direction: column;
  margin: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const TraitRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 12px;
  align-items: baseline;
  padding: 12px 2px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const TraitLabel = styled.dt`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TraitValue = styled.dd`
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  padding: 7px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.6;
  max-width: 100%;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const SplitLists = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RuleList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RuleItem = styled.li<{ $variant: 'rule' | 'avoid' }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  line-height: 1.7;
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ $variant, theme }) =>
      $variant === 'avoid' ? theme.colors.danger : theme.colors.success};
`;

const RuleMark = styled.span<{ $variant: 'rule' | 'avoid' }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 800;
  color: ${({ $variant, theme }) =>
    $variant === 'avoid' ? theme.colors.danger : theme.colors.success};
  background: ${({ $variant, theme }) =>
    $variant === 'avoid' ? theme.colors.dangerSoft : theme.colors.successSoft};
`;
